import React, { FunctionComponent, useContext } from 'react';
import { FullScreenContent } from 'components/Layouts/FullScreenView/FullScreenContent';
import { ConnectingAudio } from 'components/ConnectingAudio';
import { EventModel } from 'features/events/EventModel';
import { EventCreatorCountdown } from 'features/events/EventCreator/EventCreatorCountdown';
import { useIsEventLive } from 'features/events/eventHooks';
import { EventChat } from 'features/events//EventChat';
import { useAppSelector } from 'hooks/redux';
import { currentBroadcastFinishedSelector } from 'features/broadcast/selectors';
import { FullScreenDetails } from 'components/Layouts/FullScreenView/FullScreenDetails';
import { SquareImage } from 'components/Images/SquareImage';
import { FullScreenControls } from 'components/Layouts/FullScreenView/FullScreenControls';
import { Share } from 'components/Share';
import { EventInfo } from '../EventInfo';
import { HeartButtonWrapper } from 'components/Hearts/HeartButtonWrapper';
import { ChatButton } from 'features/chat/components';
import { EventMeta } from '../components/EventMeta';
import { ListenerCount } from 'components/ListenerCount';
import { useMediaQuery } from 'react-responsive';
import { EventChatWithPlayer } from '../EventChatWithPlayer';
import { EventChatFloating } from '../EventChatFloating';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { PrivacyNotice } from 'features/privacy/privacyNotice';

interface EventWrapperProps {
  event: EventModel | null;
}

export const EventCreatorContent: FunctionComponent<EventWrapperProps> = ({
  event
}) => {
  const isLive = useIsEventLive(event);
  const nativeEventStarted = useAppSelector(
    (state) => state.events.nativeEventStarted
  );
  const nativeEventStopped = useAppSelector(
    (state) => state.events.nativeEventStopped
  );
  const currentBroadcastFinished = useAppSelector(
    currentBroadcastFinishedSelector
  );
  const { isDACreatorAppVersion } = useContext(UserAgentContext);
  const isMobile = useMediaQuery({ query: '(max-width: 640px)' });
  const finished = currentBroadcastFinished || nativeEventStopped;
  const isLiveOrFinished = isLive || finished;
  const isSingleDAView = isDACreatorAppVersion(
    `${process.env.REACT_APP_DA_SINGLE_VIEW_MIN_VERSION}`
  );

  if (!event) {
    return null;
  }

  const renderChatView = () => {
    if (isSingleDAView) {
      return <EventChatFloating event={event} />;
    } else if (isMobile) {
      return <EventChatWithPlayer event={event} />;
    }
    return <EventChat event={event} />;
  };

  const renderMeta = () => {
    if (finished) {
      return <p className="tw-m-0">Restart your live event</p>;
    } else if (isLive) {
      return (
        <>
          <EventMeta />
          <ListenerCount />
        </>
      );
    }
    return <EventCreatorCountdown />;
  };

  const renderControls = () => {
    return (
      <FullScreenControls>
        {isLive && <HeartButtonWrapper />}
        {isLiveOrFinished && !isSingleDAView && <ChatButton />}
        <EventInfo event={event} />
        <Share title={event.title} label={'event'} />
      </FullScreenControls>
    );
  };

  const renderRightColumn = isLiveOrFinished ? renderChatView() : null;

  if (nativeEventStarted) {
    return <FullScreenContent centerColumn={<ConnectingAudio />} />;
  }

  return (
    <FullScreenContent
      centerColumn={
        <FullScreenDetails
          title={isLiveOrFinished ? event.title : null}
          image={
            <SquareImage
              artworkUrl={event.artwork.image.large}
              cssClasses={'tw-shadow-glow'}
            />
          }
          liveStatus={<PrivacyNotice />}
          meta={renderMeta()}
          controls={renderControls()}
        />
      }
      rightColumn={renderRightColumn}
    />
  );
};
